import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    OnDestroy,
    TemplateRef,
    viewChild,
    ViewContainerRef,
} from '@angular/core';
import {fromControl, TranslatePipe, TranslateService} from '@brunata-angular/util';
import {CommonModule} from '@angular/common';
import {
    DatePipe,
    InputDateRangePickerComponent,
    InputDropdownCheckboxesComponent,
    LoadComponent,
} from '@brunata-angular/ui';
import {
    BehaviorSubject,
    combineLatest,
    filter,
    Observable,
    share,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import {
    divIcon,
    DivIcon,
    Icon,
    icon,
    Map as LeafletMap,
    map as leafletMap,
    Marker,
    marker,
    MarkerCluster,
    MarkerClusterGroup,
    markerClusterGroup,
    tileLayer,
} from 'leaflet';
import {v4 as uuidv4} from 'uuid';
import {Store} from '@ngrx/store';
import {selectBuilding} from 'src/app/state/selectors';
import {map, take} from 'rxjs/operators';
import {RouterLink} from '@angular/router';
import {LocationMeter} from 'src/app/meter/meter-list/location-meter.model';
import {MeterListMapService} from 'src/app/meter/meter-list-map/meter-list-map.service';
import {DateRange} from '@brunata-angular/ui';
import {FormBuilder, FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {DateTime} from 'luxon';
import {SelectableItem} from '@brunata-angular/ui';
import {uniqBy} from 'lodash-es';
import {MeterService} from '../meter.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbCollapseModule} from '@ng-bootstrap/ng-bootstrap';
import {NgbPopover} from '@ng-bootstrap/ng-bootstrap';

type Meter = LocationMeter & {buildingNo: string};
type FilterData = {
    meterType: SelectableItem[];
    status: string[];
    events: SelectableItem[];
};

@Component({
    selector: 'app-meter-list-map',
    templateUrl: './meter-list-map.component.html',
    styleUrl: './meter-list-map.component.scss',
    imports: [
        ReactiveFormsModule,
        FormsModule,
        CommonModule,
        LoadComponent,
        TranslatePipe,
        RouterLink,
        InputDateRangePickerComponent,
        InputDropdownCheckboxesComponent,
        NgbCollapseModule,
        NgbPopover,
        DatePipe,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeterListMapComponent implements AfterViewInit, OnDestroy {
    private readonly popupTemplate = viewChild.required<TemplateRef<unknown>>('popupTemplate');
    private readonly viewContainer = viewChild.required('viewContainer', {read: ViewContainerRef});
    isCollapsed = false;
    readonly mapId = uuidv4();
    readonly isMapLoaded = new BehaviorSubject<boolean>(false);

    private map: LeafletMap | undefined;
    private meterId: string | null = null;
    private meterClusterGroup: MarkerClusterGroup | undefined;

    private readonly formBuilder = inject(FormBuilder);
    private readonly store = inject(Store);
    private readonly meterListMapService = inject(MeterListMapService);
    private readonly meterService = inject(MeterService);
    private readonly translateService = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);

    readonly dateRangeFormControl = new FormControl<DateRange>(
        {
            startDate: this.meterListMapService.startDate ?? DateTime.now().minus({months: 1}),
            endDate: this.meterListMapService.endDate ?? DateTime.now(),
        },
        {nonNullable: true},
    );

    readonly filterFormGroup = this.formBuilder.group({
        meterType: new FormControl<SelectableItem[]>(
            this.meterListMapService.meterTypeFilter ?? [],
        ),
        status: new FormControl<string[]>(this.meterListMapService.statusFilter ?? [], {
            nonNullable: true,
        }),
        // NEW CODE - Checkbox controls
        statusCheckboxes: this.formBuilder.group({
            criticalFilter: new FormControl<boolean>(false, {nonNullable: true}),
            eventFilter: new FormControl<boolean>(false, {nonNullable: true}),
            missingReadingsFilter: new FormControl<boolean>(false, {nonNullable: true}),
            noAlarmsFilter: new FormControl<boolean>(false, {nonNullable: true}),
        }),
        events: new FormControl<SelectableItem[]>([]),
    });

    private readonly meters$: Observable<Meter[]> = combineLatest([
        this.store
            .select(selectBuilding)
            .pipe(filter(buildingState => buildingState.status === 'success')),
        fromControl(this.dateRangeFormControl),
    ]).pipe(
        tap(() => this.isMapLoaded.next(false)),
        switchMap(([buildingState, dateRange]) =>
            this.meterService
                .getPhysicalMeters(
                    buildingState.buildingNo,
                    dateRange.startDate ?? DateTime.now().minus({months: 1}),
                    dateRange.endDate ?? DateTime.now(),
                )
                .pipe(
                    map(meters =>
                        meters
                            .filter(meter => meter.latitude != null && meter.longitude != null)
                            .map(meter => ({
                                ...meter,
                                buildingNo: buildingState.buildingNo,
                            })),
                    ),
                ),
        ),
        share(),
    );

    readonly filteredMeters$ = combineLatest({
        meters: this.meters$,
        filterData: this.filterFormGroup.valueChanges.pipe(
            startWith(this.filterFormGroup.value),
            map(filterData => ({
                meterType: filterData.meterType ?? [],
                status: filterData.status ?? [],
                events: filterData.events ?? [],
            })),
        ),
    }).pipe(map(({filterData, meters}) => this.filterMeters(meters, filterData)));

    constructor() {
        const savedFilter = this.meterListMapService.statusFilter ?? [];
        this.filterFormGroup.controls.statusCheckboxes.patchValue(
            {
                criticalFilter: savedFilter.includes('criticalFilter'),
                eventFilter: savedFilter.includes('eventFilter'),
                missingReadingsFilter: savedFilter.includes('missingReadingsFilter'),
                noAlarmsFilter: savedFilter.includes('noAlarmsFilter'),
            },
            {emitEvent: false},
        );
        this.filterFormGroup.patchValue(
            {
                status: savedFilter,
            },
            {emitEvent: false},
        );

        // NEW CODE - Sync checkbox changes to status array for consistent filtering
        this.filterFormGroup.controls.statusCheckboxes.valueChanges
            .pipe(takeUntilDestroyed())
            .subscribe(checkboxes => {
                const selectedStatuses = Object.entries(checkboxes)
                    .filter(([_, checked]) => checked)
                    .map(([key, _]) => key);
                this.filterFormGroup.patchValue({
                    status: selectedStatuses,
                });
            });

        // Only patch form values ONCE when meters first load
        this.meters$.pipe(take(1), takeUntilDestroyed()).subscribe(meters => {
            this.patchFormGroupValues(meters);
        });
    }

    ngAfterViewInit(): void {
        // Use setTimeout because the map won't draw correctly without it.
        setTimeout(() => {
            if (this.map != null) {
                throw new Error('Map already initialized');
            }
            const myMap = leafletMap(this.mapId, {
                center: this.meterListMapService.center ?? [55, 11],
                zoom: this.meterListMapService.zoom ?? 5,
                markerZoomAnimation: true,
            });
            myMap.attributionControl.setPrefix('');
            myMap.zoomControl.setPosition('topright');
            tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                minZoom: 3,
                maxZoom: 19,
                attribution:
                    '<a href="https://leafletjs.com/" target="_blank">Leaflet</a> | <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
            }).addTo(myMap);
            this.map = myMap;
            this.meterClusterGroup = markerClusterGroup({
                maxClusterRadius: 60,
                spiderfyOnMaxZoom: true,
                showCoverageOnHover: false,
                iconCreateFunction: cluster => this.createMarkerClusterIcon(cluster),
            });
            myMap.addLayer(this.meterClusterGroup);

            this.filteredMeters$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(meters => {
                this.meterClusterGroup?.clearLayers();
                if (this.meterClusterGroup != null) {
                    this.meterClusterGroup.addLayers(
                        this.createMeterMarkers(meters, this.meterClusterGroup),
                    );
                    if (
                        this.meterListMapService.center == null &&
                        this.meterClusterGroup.getBounds().isValid()
                    ) {
                        myMap.fitBounds(this.meterClusterGroup.getBounds(), {maxZoom: 17});
                    }
                    this.isMapLoaded.next(true);
                }
            });
        });
    }

    private createMeterMarkers(meters: Meter[], meterClusterGroup: MarkerClusterGroup): Marker[] {
        return meters
            .filter(meter => meter.latitude != null && meter.longitude != null)
            .map(meter => {
                const meterMarker = marker([meter.latitude ?? 0, meter.longitude ?? 0], {
                    icon: this.getIcon(this.getIconColor(meter)),
                })
                    .on('click', () => {
                        // Create popup on demand since it is too slow to create all popups in advance.
                        this.createAndOpenPopup(meterMarker, meter);
                    })
                    .on('popupopen', () => {
                        this.meterId = meter.meterId;
                    })
                    .on('popupclose', () => {
                        if (this.meterId === meter.meterId) {
                            this.meterId = null;
                        }
                    });

                if (this.meterListMapService.meterId === meter.meterId) {
                    setTimeout(() => {
                        meterClusterGroup.zoomToShowLayer(meterMarker, () => {
                            this.createAndOpenPopup(meterMarker, meter);
                            this.meterListMapService.meterId = null;
                            this.meterListMapService.eventsFilter =
                                this.filterFormGroup.value.events ?? [];
                            this.meterListMapService.meterTypeFilter =
                                this.filterFormGroup.value.events ?? [];
                        });
                    }, 0);
                }
                return meterMarker;
            });
    }

    private createAndOpenPopup(meterMarker: Marker, meter: Meter): void {
        if (meterMarker.getPopup() == null) {
            meterMarker.bindPopup(this.createPopupTemplate(meter), {
                closeButton: false,
                maxWidth: 500,
            });
        }
        // Use setTimeout to make the popup render with the correct width.
        setTimeout(() => {
            meterMarker.openPopup();
        });
    }

    private saveFilterSettings(): void {
        this.meterListMapService.zoom = this.map?.getZoom();
        this.meterListMapService.center = this.map?.getCenter();
        this.meterListMapService.meterId = this.meterId;

        this.meterListMapService.statusFilter = this.filterFormGroup.value.status ?? [];

        this.meterListMapService.eventsFilter =
            this.filterFormGroup.value.events?.filter(it => it.selected) ?? [];
        this.meterListMapService.meterTypeFilter =
            this.filterFormGroup.value.meterType?.filter(it => it.selected) ?? [];
        this.meterListMapService.startDate = this.dateRangeFormControl.value.startDate ?? undefined;
        this.meterListMapService.endDate = this.dateRangeFormControl.value.endDate ?? undefined;
    }

    ngOnDestroy(): void {
        this.saveFilterSettings();
        // Remove map to avoid issue "Map container is already initialized", when changing page
        this.map?.remove();
    }

    private createPopupTemplate(meter: Meter): HTMLElement {
        const context = {
            meter: meter,
            routerLink: `/admin/system/${meter.buildingNo}/meters/meters-and-sensors/${meter.meterId}`,
        };
        const embeddedView = this.viewContainer().createEmbeddedView(this.popupTemplate(), context);
        const popupHTML = document.createElement('div');
        popupHTML.append(...embeddedView.rootNodes);
        return popupHTML;
    }

    private patchFormGroupValues(meters: Meter[]): void {
        this.filterFormGroup.patchValue({
            meterType: uniqBy(meters, meter => meter.meterSuperTypeNo).map(meter => ({
                name: this.translateService.translate(
                    'mappers_meterType.' + meter.meterSuperTypeNo,
                    'online',
                    'common',
                ),
                selected:
                    this.meterListMapService.meterTypeFilter?.some(
                        meterType =>
                            meterType.selected &&
                            meterType.name ===
                                this.translateService.translate(
                                    'mappers_meterType.' + meter.meterSuperTypeNo,
                                    'online',
                                    'common',
                                ),
                    ) ?? false,
            })),

            events: uniqBy(
                meters
                    .flatMap(meter => meter.eventTypesInPeriod)
                    .filter(event => event?.severity !== 'CRITICAL'),
                event => event?.eventType,
            ).map(event => ({
                name: this.translateService.translate('event-alarms_' + event?.eventType),
                selected:
                    this.meterListMapService.eventsFilter?.some(
                        eventType =>
                            eventType.selected &&
                            eventType.name ===
                                this.translateService.translate('event-alarms_' + event?.eventType),
                    ) ?? false,
            })),
        });
    }

    private filterMeters(meters: Meter[], filterData: FilterData): Meter[] {
        // TODO: filterMeters is for unknown reasons called three times when new meters are received. It only takes
        //  few milliseconds each time, so we can live with this flaw for now.
        return meters.filter(meter => {
            const meterTypeFilter = this.filterMeterEventAlarmsFromList(
                this.translateService.translate(
                    'mappers_meterType.' + meter.meterSuperTypeNo,
                    'online',
                    'common',
                ),
                filterData.meterType,
            );
            const hasNoEvents = !meter.eventTypesInPeriod.some(
                event => event?.severity === 'CRITICAL' || event?.severity === 'WARNING',
            );
            if (filterData.status.length === 0) {
                return meterTypeFilter;
            }
            const statusMatches = filterData.status.some(status => {
                switch (status) {
                    case 'criticalFilter':
                        return meter.eventTypesInPeriod.some(
                            event => event?.severity === 'CRITICAL',
                        );
                    case 'eventFilter': {
                        return meter.eventTypesInPeriod
                            .map(
                                event =>
                                    this.filterMeterEventAlarmsFromList(
                                        this.translateService.translate(
                                            'event-alarms_' + event?.eventType,
                                        ),
                                        filterData.events,
                                    ) && event?.severity !== 'CRITICAL',
                            )
                            .some(event => event);
                    }
                    case 'missingReadingsFilter':
                        return (
                            meter.hasReadingsInPeriod != null &&
                            !meter.hasReadingsInPeriod &&
                            hasNoEvents
                        );
                    case 'noAlarmsFilter':
                        return meter.hasReadingsInPeriod === true && hasNoEvents;
                    default:
                        return false;
                }
            });

            return statusMatches && meterTypeFilter;
        });
    }

    private filterMeterEventAlarmsFromList(
        value: string | null,
        filterValue: SelectableItem[] | null,
    ): boolean {
        const valuesToFilter = filterValue
            ?.filter(selectableItem => selectableItem.selected)
            .map(selectableItem => selectableItem.name.toLowerCase());
        if (value === null || valuesToFilter?.length === 0) {
            return true;
        }
        return valuesToFilter?.includes(value.toLowerCase()) ?? true;
    }

    private getIconColor(meter: Meter): 'green' | 'red' | 'yellow' | 'grey' {
        if (meter.eventTypesInPeriod.some(event => event?.severity === 'CRITICAL')) {
            return 'red';
        }
        if (meter.eventTypesInPeriod.some(event => event?.severity === 'WARNING')) {
            return 'yellow';
        }
        if (meter.hasReadingsInPeriod === false) {
            return 'grey';
        }
        return 'green';
    }

    private getIcon(color: 'red' | 'yellow' | 'green' | 'grey'): Icon {
        return icon({
            className: `custom-div-icon ${color}`,
            iconUrl: `./assets/markers/marker-icon-${color}.png`,
            shadowUrl: './assets/markers/marker-shadow.png',
            iconAnchor: [12, 40],
            popupAnchor: [0, -40],
        });
    }

    private createMarkerClusterIcon(cluster: MarkerCluster): DivIcon {
        return divIcon({
            html: `<div style="border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; background: ${this.getBackgroundColor(cluster)};">
                                ${cluster.getChildCount()}
                               </div>`,
            className: 'custom-cluster-icon',
            iconSize: [30, 30],
        });
    }

    private getColorCodeFromColor(color: string): string {
        switch (color) {
            case 'green':
                return 'rgb(44,173,37, 0.7)';
            case 'red':
                return 'rgb(202,40,63, 0.7)';
            case 'yellow':
                return 'rgba(255,255,0,0.7)';
            case 'grey':
                return 'rgb(135,135,135, 0.7)';
            default:
                return 'rgb(41,129,202, 0.7)';
        }
    }

    private getBackgroundColor(cluster: MarkerCluster): string {
        const classNames = cluster
            .getAllChildMarkers()
            .map(myMarker => myMarker.getIcon().options.className);
        if (
            classNames.every(
                className =>
                    className?.replace('custom-div-icon ', '') ===
                    classNames[0]?.replace('custom-div-icon ', ''),
            )
        ) {
            return this.getColorCodeFromColor(
                cluster
                    .getAllChildMarkers()[0]
                    .getIcon()
                    .options.className?.replace('custom-div-icon ', '') ?? '',
            );
        }

        const colorOrder = ['green', 'yellow', 'red', 'grey'];

        const clusterColors = [
            ...new Set(
                cluster
                    .getAllChildMarkers()
                    .map(
                        cl => cl.getIcon().options.className?.replace('custom-div-icon ', '') ?? '',
                    ),
            ),
        ].sort((a, b) => colorOrder.indexOf(a) - colorOrder.indexOf(b));

        const gradientStops = clusterColors
            .map((color, index) =>
                index === 0
                    ? `${this.getColorCodeFromColor(color)} 5%`
                    : this.getColorCodeFromColor(color),
            )
            .join(',');

        return `linear-gradient(to right,${gradientStops})`;
    }
}
